import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Link } from 'react-router-dom';
import StyledComponent from 'styled-components';

import Colors from '../../constant/colors';
import {
  FormInputChange,
  FormInputProps,
  FormInputTypeEnum,
  FormInputValidation,
  FormInputValuesMap,
  FormValidatedProps,
  FormValidatedRef,
} from './Form.types';
import FormFooter from './FormFooter';
import FormInput from './FormInput';

const btnColor = '#70a040';

const FormValidatedContainer = StyledComponent.form<{ columns?: number }>`
  border-radius:     5px;
  background-color:  rgb(248, 248, 247);
  padding:           20px;
  max-height:        100%;
  overflow-y:        scroll;
  margin:            auto;
  border:            0.5px solid lightgray;
  color:             #FFFFFF;

  p
  {
    color:      #2F2734;
    text-align: initial;
  }

  label
  {
    float: left;
    color: ${Colors.form.formInputLabel.color};
  }

  .form-input-row{
    width: ${({ columns = 1 }) => (columns === -1 ? 'unset' : `${(100 - 2 * columns) / columns}%`)};

    display: ${({ columns }) => (columns === -1 ? 'flex' : 'inline-block')};
    flex-wrap: wrap;
    margin-right: 1%;
    margin-left: 1%;
    margin-bottom: 1rem;

    label {
      width: 100%;
    }
  }

  /* Input field styling */
  input[type=text], input[type=email],
  input[type=number], input[type=file],
  select, textarea,
  input[type=password]
  {
    display: block;
    width: 100%;
    height: calc(1.5em + 0.75rem + 2px);
    padding:0.375rem 1.6rem;
    font-size:1rem;
    font-weight:400;
    line-height:1.5;
    color:#495057;
    background-color:#fff;
    background-clip:padding-box;
    border:1px solid #ced4da;
    border-radius: 0.25rem;
    transition:border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }

  input[type=submit], button
  {
    color:          #FFF !important;
    background:     ${btnColor} !important;
    border-color:   ${btnColor} !important;
    padding:        12px 20px;
    border-radius:  4px;
    cursor:         pointer;
    border-width:   0.5px;
    border-style:   solid;
  }

  select /* Chrome likes to think you don't really want to apply padding to select elements */
  {
    -webkit-appearance: none;
    -moz-appearance:    none;
    appearance:         none;
  }
  
  /* Drop down indicator */
  .dropdown:before
  {
    content: "";
    position: absolute;
    right: 10px;
    top: 55%;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid #000;
  }

  .tooltip-question
  {
    font-weight: 500 !important;
    color: #007bff !important;
    float: right;
    cursor: pointer;
  }
`;

const FormFooterContainer = StyledComponent.div`
  .form-footer
  {
    border-bottom-right-radius: 10px;
    border-bottom-left-radius: 10px;
    background: ${Colors.form.formInputLabel.color};
    padding: 20px 0px;
    font-family: Playfair Display;
    max-width: 420px;
    margin: auto;
  }

  h3
  {
    color: #fff;
    text-align: center;
    font-size: 30px;
  }
`;

const FORM_TYPES = ['_form_footer_', 'space', 'link'];

const FormValidated = forwardRef<FormValidatedRef, FormValidatedProps>(
  (
    {
      columns,
      description,
      dynamicInputs,
      formOnChange,
      formInputs,
      validateOnInit,
    }: FormValidatedProps,
    formRef,
  ) => {
    /**
     * Key is form input ID and value is form input value
     */
    const [formInputValuesMap, setFormInputValuesMap] = useState<FormInputValuesMap>({});
    const [isFormValid, setIsFormValid] = useState<boolean>(false);

    useEffect(() => {
      formInputs.forEach(input => {
        if (!FORM_TYPES.includes(input.type)) {
          setFormInputValuesMap(prev => ({
            ...prev,
            [input.id]: {
              value: input.value || input.value === false ? input.value : '',
              error:
                input.required === 'required' && !['button', 'submit'].includes(input.type)
                  ? '-EV'
                  : undefined,
            },
          }));
        }
      });
      setIsFormValid(false);

      if (validateOnInit) {
        formInputs.forEach(input => {
          if (!FORM_TYPES.includes(input.type)) {
            formInputOnChange(input);
          }
        });
      }
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    const formInputOnChange = (change: FormInputChange, notifyParent = true) => {
      // Check for error in validation
      const error = validateField(change.id, change.value, change.validation, change.type);

      // Save value and error state
      setFormInputValuesMap(prev => ({
        ...prev,
        [change.id]: {
          ...(prev?.[change.id] ? prev[change.id] : {}),
          error,
          value: change.value,
        },
      }));

      // Check if form is valid
      const isValid = Object.keys(formInputValuesMap).every(k => {
        if (formInputValuesMap[k].error?.length) {
          console.log(formInputValuesMap[k]);
          return false;
        }
        return true;
      });
      setIsFormValid(isValid);

      // Tell buttons about isValid state change
      formInputs
        .filter(({ type }) => ['button', 'submit'].includes(type))
        .forEach(btnInput => {
          setFormInputValuesMap(prev => ({
            ...prev,
            [btnInput.id]: {
              ...(prev?.[btnInput.id] ? prev[btnInput.id] : {}),
              value: btnInput.value,
              isDisabled: !isValid,
            },
          }));
        });

      // Update parent page of change in form state
      if (notifyParent) {
        formOnChange(change, isValid);
      }
    };

    // MARK: - Validation
    /**
	 * Validate form field
	 *
	 * @param   {string}    id            		Id of field
	 * @param 	{string} 	value 				Value in field to validate
	 * @param 	{JSON}		validation 			JSON object with validation algorithm return true/false
   @param   {String}    type    The type of field this is
	 *				algorithm: email|range
	 *				params: [range: min,max]
	 *
	 * Return empty string if valid or error message if invalid
	 *
	 */
    const validateField = (
      id: string,
      value: any,
      validation: FormInputValidation | undefined,
      type: string,
    ) => {
      let errorMessage;
      if (validation && validation.algorithm) {
        // console.log('Table.validateField(' + id + ', ' + value + ")");
        switch (validation.algorithm) {
          case 'email':
            errorMessage = value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)
              ? ''
              : 'This email address is invalid';
            break;
          case 'range':
            if (!!validation.min && !!validation.max) {
              if (type === 'number') {
                // Handle numeric values
                if (Number.isNaN(value)) {
                  errorMessage = `${id} is not a valid number`;
                } else if (value < validation.min) {
                  errorMessage = `${id} is too short.  Please enter at least a value of ${validation.min}`;
                } else if (value > validation.max) {
                  errorMessage = `${id} is too long.  Please enter at most a value of ${validation.max}`;
                }
              } else if (value === undefined || value.length < validation.min) {
                // Handle strings
                errorMessage = `${id} is too short.  Please enter at least ${validation.min} characters`;
              } else if (value === undefined || value.length > validation.max) {
                if (type !== 'datetime' && type !== 'date') {
                  errorMessage = `${id} is too long.  Please enter at most ${validation.max} characters`;
                }
              }
            }
            break;
          case 'select':
            if (value && value.length === 0) {
              errorMessage = `${id} must have a selection`;
            }
            break;
          default:
            break;
        }
      }

      return errorMessage;
    };

    useImperativeHandle(formRef, () => ({
      getValueFor: (id: string) =>
        // console.log(id);
        //  console.log(formInputValuesMap);
        formInputValuesMap[id]?.value,
      reset,
    }));

    // Clear fields and errors
    const reset = () => {
      formInputs.forEach(input => {
        if (!FORM_TYPES.includes(input.type)) {
          setFormInputValuesMap(prev => ({
            ...prev,
            [input.id]: {
              value: input.value || input.value === false ? input.value : '',
              error:
                input.required === 'required' && !['button', 'submit'].includes(input.type)
                  ? '-EV'
                  : undefined,
            },
          }));
        }
      });

      formInputs
        .filter(({ type }) => ['button', 'submit'].includes(type))
        .forEach(btnInput => {
          setFormInputValuesMap(prev => ({
            ...prev,
            [btnInput.id]: {
              ...(prev?.[btnInput.id] ? prev[btnInput.id] : {}),
              value: false,
            },
          }));
        });
    };

    /**
    Manually update form input values
    (Useful if already instantiated form inputs and manually modified their value)
    because form inputs use their state's value which is set on construction
    @param  String  id      The id of the form input
    @param  String  value    The new value to assign to it
  */
    const updateFormInputValue = async (
      id: string,
      value: any,
      validation: FormInputValidation | undefined,
      type: FormInputTypeEnum,
    ) => {
      formInputs.forEach(input => {
        if (!FORM_TYPES.includes(input.type)) {
          formInputOnChange({
            id,
            value,
            validation,
            type,
          });
        }
      });
    };

    /**
     * Wrapper around onClick so we can pass forminput values into the button onpress
     */
    const onClickWrapper = (
      onClick: FormInputProps['onClick'],
      evt: any,
      data: { successActions?: any; route?: string } = {},
    ) => {
      onClick &&
        onClick(
          evt,
          data,
          formInputs.map(({ id, value, type, managedUpdateForm }) => ({
            id,
            value,
            type,
            managedUpdateForm,
          })),
        );
    };

    useEffect(() => {
      if (!dynamicInputs) {
        // If we do not support dynamic inputs skip all this validation
        return;
      }

      // First render
      if (!formInputValuesMap) {
        return;
      }

      // Check if ID's have changed (someone manually altered inputs)
      formInputs.forEach(input => {
        if (!FORM_TYPES.includes(input.type)) {
          // New input added
          if (!formInputValuesMap[input.id] || formInputValuesMap[input.id].value !== input.value) {
            const isButton = ['button', 'submit'].includes(input.type);
            const isRequired = input.required === 'required';
            const hasValue =
              input.value?.toString() !== undefined && input.value?.toString().length > 0;
            const error = isRequired && !isButton && !hasValue ? '-EV' : undefined;
            console.log(`${input.id} input value ${input.value} has value ${hasValue}`);

            setFormInputValuesMap(prev => ({
              ...prev,
              [input.id]: {
                value: hasValue ? input.value : '',
                error,
              },
            }));
          }
        }
      });

      // If we have to remove a form input we should force all inputs to update themselves
      let forceFormInputValuesUpdate = false;
      Object.keys(formInputValuesMap).forEach(k => {
        const existsInFormInputs = formInputs.find(input => input.id === k);
        if (!existsInFormInputs) {
          forceFormInputValuesUpdate = true;
          // Form input no longer exists in the form
          setFormInputValuesMap(prev => {
            const newMap = { ...prev };
            delete newMap[k];
            return newMap;
          });
        }
      });

      // Form inputs values were modified by parent after they were already instantiated
      // so we must tell them to all update their state
      if (forceFormInputValuesUpdate) {
        formInputs.forEach(input => {
          updateFormInputValue(input.id, input.value, input.validation, input.type);
        });
      }
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [formInputs]);

    let formInputComponents: any[] = [];
    let formFooter = null;

    // User did not customize grid layout, make everything single row
    if (columns !== -1 && formInputs) {
      formInputComponents = formInputs
        .filter(input => input !== null)
        .map(input => {
          // @ts-ignore
          if (input.type === '_form_footer_') {
            formFooter = input;
            return '';
          }

          return (
            // @ts-ignore
            input.type === 'link' ? (
              <Link
                key={input.id}
                className="btn-link"
                // @ts-ignore
                onClick={input.onClick}
                // @ts-ignore
                to={input.to}>
                {input.text}
              </Link>
            ) : (
              <FormInput
                {...input}
                onClickWrapper={input.onClick ? onClickWrapper : undefined}
                key={input.id}
                isDisabled={
                  input.type === 'submit' ? formInputValuesMap[input.id]?.isDisabled : false
                }
                rowClassName="form-input-row"
                formInputOnChange={formInputOnChange}
                errorMessage={formInputValuesMap[input.id]?.error ?? ''}
                value={formInputValuesMap[input.id]?.value ?? ''}
              />
            )
          );
        });
    } else {
      // User specified custom grid layout (-1)
      const completedRows: any[] = [];
      formInputComponents = formInputs.map((input, i) => {
        // @ts-ignore
        if (input.type === '_form_footer_') {
          formFooter = input;
          return '';
        }
        const { row } = formInputs[i];
        if (completedRows.indexOf(row) !== -1) {
          return '';
        }

        // Find all columns in this row
        const columnsInRow = [input];
        const upperBound = formInputs.length - 1;
        let nextRow = i + 1;
        while (nextRow <= upperBound && formInputs[nextRow].row === formInputs[i].row) {
          columnsInRow.push(formInputs[nextRow]);
          nextRow++;
        }

        completedRows.push(row);

        return (
          <div className="form-input-row" key={`form-validated-row-${i}`}>
            {columnsInRow.map((formInput, j) => (
              <div className={`col-lg-${formInput.col}`} key={`form-validated-column-${i}-${j}`}>
                {/* @ts-ignore */}
                {formInput.type === 'link' && formInput.to ? (
                  <Link
                    key={`form-validated-link-${i}-${j}`}
                    className="btn-link"
                    // onClick={formInput.onClick}
                    to={`/${formInput.to}`}>
                    {formInput.text}
                  </Link>
                ) : // @ts-ignore
                formInput.type === 'space' ? (
                  ''
                ) : (
                  <FormInput
                    {...formInput}
                    onClickWrapper={formInput.onClick ? onClickWrapper : undefined}
                    key={`form-validated-input-${i}-${j}`}
                    rowClassName="form-input-row"
                    formInputOnChange={formInputOnChange}
                    errorMessage={formInputValuesMap[input.id]?.error ?? ''}
                    value={formInputValuesMap[input.id]?.value ?? ''}
                  />
                )}
              </div>
            ))}
          </div>
        );
      });
    }

    //    console.log(formInputs);
    return (
      <>
        <FormValidatedContainer
          action="#"
          data-is-form-valid={isFormValid}
          autoComplete="off"
          columns={columns}>
          <div className={`form-group row ${!description ? 'hidden' : ''}`}>
            <div className="col-lg-12">
              <p>{description}</p>
            </div>
          </div>

          {formInputComponents}
        </FormValidatedContainer>
        {formFooter && (
          <FormFooterContainer>
            {/* @ts-ignore */}
            <FormFooter title={formFooter.title} />
          </FormFooterContainer>
        )}
      </>
    );
  },
);

export default FormValidated;
